import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/nodes';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 免费节点获取指南',
  description: '节点指南致力于为全球华语用户提供最新、最稳定的免费节点资源与专业技术教程。我们每日更新节点信息，帮助用户快速找到可用的免费节点，告别频繁断线烦恼。',
  url: 'https://your-domain.com/about',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '节点指南',
  url: 'https://your-domain.com/',
  description: '免费节点获取指南·每日更新稳定可用 — 提供免费节点获取渠道、筛选方法与使用技巧，涵盖稳定性测试、速度优化及安全注意事项。',
};

const reviewSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '节点指南',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: testimonials.length.toString(),
    bestRating: '5',
  },
  review: testimonials.map((t) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: t.name },
    reviewBody: t.content,
    reviewRating: { '@type': 'Rating', ratingValue: t.rating.toString(), bestRating: '5' },
  })),
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9rc34fnahb7382ovkb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>

      <div className="min-h-screen bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mt-6 mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">关于我们</h1>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    节点指南创立于 2023 年，致力于为全球华语用户提供最新、最稳定的免费节点资源与专业技术教程。
                    我们深知寻找可靠免费节点的困难——信息分散、质量参差不齐、更新不及时。
                    因此，我们组建了一支专业的内容编辑团队，每日从多个渠道收集、验证和筛选节点信息，
                    确保每一位访问者都能找到当前可用的优质节点资源。
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    我们的团队由网络安全研究员、后端工程师和技术写作者组成，对代理协议（SSR、V2Ray、Trojan 等）
                    有着深入的理解和丰富的实战经验。我们不仅提供节点信息，更注重帮助用户理解底层原理，
                    培养安全使用习惯。
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    截至目前，节点指南已累计发布超过 50 篇原创教程，整理的节点资源帮助了数万名用户。
                    我们将继续坚持"准确、及时、实用"的原则，为你带来最优质的免费节点获取指南。
                  </p>
                </div>
                <div className="w-full md:w-80 shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20warm%20coral%20and%20amber%20accent%20decor%2C%20clean%20desk%20with%20laptop%20and%20notebook%2C%20soft%20natural%20lighting%20from%20large%20window%2C%20cozy%20professional%20atmosphere%2C%20editorial%20photography%20style%20with%20warm%20tones%2C%20no%20people%2C%20no%20text&width=800&height=600&seq=about-brand-2026&orientation=portrait"
                    alt="节点指南品牌 - 现代简约办公环境"
                    className="w-full h-56 object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <p className="text-foreground-600 mb-6">
                如果你有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在 24 小时内回复。
              </p>

              {formStatus === 'success' ? (
                <div className="flex items-center gap-3 bg-accent-100 text-accent-700 px-5 py-4 rounded-lg">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                  </div>
                  <span className="text-sm font-medium">感谢你的留言！我们会尽快与你取得联系。</span>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 *
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 *
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请选择或输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入你的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多 500 个字符</p>
                  </div>
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-3 bg-red-50 text-red-700 px-5 py-4 rounded-lg">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-error-warning-fill text-lg"></i>
                      </div>
                      <span className="text-sm font-medium">{formError}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line"></i>
                    发送留言
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}